import * as React from 'react';
import { Container, Grid, Box, Typography, Button } from '@mui/material';
import samsungLogo from '../../assets/logo/samsung-logo.png';
// import oppoLogo from '../../assets/logo/oppo-logo.png';
import appleLogo from '../../assets/logo/apple-logo.png';
import appleCassePng from '../../assets/images/apple-casse.png';
// 👇 Optionnel mais recommandé : ajoute aussi un WebP à côté du PNG
// (si tu ne l’as pas, laisse la ligne commentée, ça marchera avec le PNG)
// import appleCasseWebp from '../../assets/images/apple-casse.webp';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import heroI18n from '../../i18n/components/services/hero.i18n';

export default function Hero() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('services', heroI18n); }, [register]);

  // === Preload image ASAP (works even before it's scrolled into view)
  React.useEffect(() => {
    const href = /* appleCasseWebp || */ appleCassePng; // prefer webp if you have it
    // Avoid duplicating the tag if we remount
    const existing = [...document.head.querySelectorAll('link[rel="preload"]')]
      .find(l => l.getAttribute('as') === 'image' && l.getAttribute('href') === href);
    if (existing) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    // Helps some browsers prioritize hero imagery
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  // Fade-in once loaded (nice polish)
  const [imgReady, setImgReady] = React.useState(false);

  return (
    <Box
      sx={{
        bgcolor: '#0466c8',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 5, sm: 6, md: 10, lg: 12 },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        <Grid container alignItems="center">
          {/* Colonne gauche */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxWidth: { xs: 720, md: 750 },
                mx: { xs: 'auto', md: 0 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {/* Titre sur deux lignes */}
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 32, sm: 38, md: 55 },
                  fontWeight: 800,
                  lineHeight: 1.15,
                  mb: { xs: 2.5, md: 2 },
                }}
              >
                <Box component="div" sx={{ color: '#ffffffff' }}>
                  {t('services.hero.title.l1')}
                </Box>
                <Box component="div" sx={{ color: '#ffffffff' }}>
                  {t('services.hero.title.l2')}
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: 17, md: 19 },
                  color: '#ffffffff',
                  mb: { xs: 3, md: 4 },
                  maxWidth: { xs: '100%', md: 650 },
                  mx: { xs: 'auto', md: 0 },
                }}
              >
                {t('services.hero.subtitle')}
              </Typography>

              {/* CTA + Logos */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'space-between' },
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2.5, md: 2 },
                  textAlign: 'center',
                }}
              >
                <Button
                  href="/tarifs"
                  variant="contained"
                  sx={{
                    backgroundColor: '#fff',
                    color: '#0466c8',
                    fontWeight: 800,
                    textTransform: 'none',
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    borderRadius: 50,
                    px: 4.5,
                    py: 1.4,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    transition: 'all 120ms ease',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.25)',
                    },
                  }}
                >
                  {t('services.hero.cta')}
                </Button>

                {/* Logos */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 3, md: 4 },
                  }}
                >
                  {/* <Box component="img" src={oppoLogo} alt="Oppo" sx={{ height: { xs: 18, md: 22 }, width: 'auto' }} /> */}
                  <Box component="img" src={samsungLogo} alt="Samsung" sx={{ height: { xs: 28, md: 35 }, width: 'auto' }} />
                  <Box
                    component="img"
                    src={appleLogo}
                    alt="Apple"
                    sx={{
                      height: { xs: 24, md: 30 },
                      width: 'auto',
                      position: 'relative',
                      top: '-5px',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Image à droite (cachée si largeur < 1000px) */}
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            bottom: { md: '-120px', lg: '-140px' },
            pointerEvents: 'none',
            '@media (max-width:1000px)': {
              display: 'none',
            },
          }}
        >
          {/* Use <picture> so WebP is chosen when available */}
          <picture>
            {/* Décommente si tu as le WebP
            <source srcSet={appleCasseWebp} type="image/webp" />
            */}
            <Box
              component="img"
              // src fallback png
              src={appleCassePng}
              alt={t('services.hero.alt.brokenIphone')}
              // ⬇️ performance hints
              loading="eager"
              decoding="async"
              fetchpriority="high"
              // Donne des dimensions intrinsèques pour éviter le reflow
              width={480}
              height={480}
              // Fade-in lorsque l’image est décodée
              onLoad={() => setImgReady(true)}
              onLoadStart={() => setImgReady(false)}
              sx={{
                height: { md: 430, lg: 480 },
                width: 'auto',
                display: 'block',
                opacity: imgReady ? 1 : 0,
                transition: 'opacity 200ms ease',
                willChange: 'opacity',
              }}
            />
          </picture>
        </Box>
      </Container>
    </Box>
  );
}